import { Link as ChakraLink } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import {
  MdDashboard,
  MdOutlineAccountTree,
  MdOutlineDns,
} from "react-icons/md";
import {
  MdsCalendarMonthRound,
  MdsRateReviewRound,
} from "react-icons-with-materialsymbols/mds";
import {
  Link as ReactRouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import { AssistantIcon } from "@/components/icons/assistant-icon.tsx";
import { LogoWithText } from "@/components/icons/logo-text.tsx";
import { Logo } from "@/components/icons/logo.tsx";
import { Icon } from "@/design/components/icon";
import {
  NavigationPanel,
  NavigationPanelBody,
  NavigationPanelFooter,
  NavigationPanelHeader,
  NavigationPanelItem,
  NavigationPanelToggle,
} from "@/design/components/navigation-panel";
import {
  getOnGoingAnalysisDownloads,
  getOnGoingAnalysisUploads,
  getStudioAnalysis,
} from "@/features/data-manager/redux";
import { currentWorkflowId } from "@/features/workflow-studio/redux/workflow-slice";
import { useAppDispatch, useAppSelector } from "@/reduxHooks.ts";
import { openModal } from "@/slices/modal-slice/modal-slice";
import { ModalTypes } from "@/slices/modal-slice/modals-constant";
import { setSidebarOpen } from "@/slices/sidebar-slice";
import { FEEDBACK_TYPE } from "@/utils/enums";
import { CacheKey, cacheUtil } from "@/utils/localstorage.ts";

const AskClootrackIcon = () => {
  return <AssistantIcon />;
};

export const WorkflowSideNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state } = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();

  const workflowId = useAppSelector(currentWorkflowId);
  const studioAnalysis = useAppSelector(getStudioAnalysis);
  const ongoingDownloads = useAppSelector((s) =>
    getOnGoingAnalysisDownloads(s, params.analysisId!)
  );

  const [showGenie, setShowGenie] = useState(false);
  const isDataManger = pathname.includes("data-manager");

  const uploads = useAppSelector((s) =>
    getOnGoingAnalysisUploads(s, params.analysisId!)
  );

  const onToggleSidebar = (sidebarOpen: boolean) => {
    dispatch(setSidebarOpen(sidebarOpen));
  };

  // const { uploads: ioRecords } = useOngoingUploads({ showReview: false });

  useEffect(() => {
    if (!studioAnalysis) {
      setShowGenie(false);
    } else if (import.meta.env.VITE_DISABLED_GENIE_TENANTS) {
      const tenantToken = cacheUtil.getUnParsed(CacheKey.TenantId) ?? {};
      const disabledTenants = import.meta.env.VITE_DISABLED_GENIE_TENANTS.split(
        ","
      );
      if (disabledTenants.includes(tenantToken as string)) {
        setShowGenie(false);
      } else {
        setShowGenie(true);
      }
    } else {
      setShowGenie(true);
    }
  }, [studioAnalysis]);

  const navigateToAskclootrack = () => {
    if (!allowNavigate() || !studioAnalysis) return;
    console.log("Attempting to navigate using React Router");

    // Extract the analysis ID from the current URL
    const analysisId = pathname.split("/analysis/")[1]?.split("/")[0];

    if (!analysisId) {
      console.error("Unable to extract analysis ID from current URL");
    }

    // Construct the new path
    const newPath = `/analysis/${analysisId}/genie/home`;

    // Perform the navigation
    navigate(newPath);
  };

  const isActive = (name: string) => {
    return pathname.includes(name);
  };

  // const hasSheetSelection = useMemo(() => {
  //   const hasPendingSelection = ioRecords?.some(
  //     (record) => record.state === DATA_UPLOAD_STATUS.SELECTION_PENDING
  //   );
  //   return hasPendingSelection;
  // }, [ioRecords]);

  const allowNavigate = () => {
    if (uploads && uploads.length > 0) {
      const userInput = confirm(
        "Upload is in progress, Exiting this page would cancel the ongoing upload.\nAre you sure you want to proceed?"
      );
      if (!userInput) return false;
    } else if (ongoingDownloads && ongoingDownloads?.length > 0) {
      const userInput = confirm(
        "Alert! Exiting this page will cancel your download.\nAre you sure you want to proceed?"
      );
      if (!userInput) return false;
    }
    return true;
  };

  const LogoTextLink = () => {
    return (
      <ChakraLink
        as={ReactRouterLink}
        onClick={(e) => {
          if (!allowNavigate()) {
            e.preventDefault();
          }
        }}
        to="/home"
      >
        <LogoWithText />
      </ChakraLink>
    );
  };

  const LogoLink = () => {
    return (
      <ChakraLink
        as={ReactRouterLink}
        onClick={(e) => {
          if (!allowNavigate()) {
            e.preventDefault();
          }
        }}
        to="/home"
      >
        <Logo />
      </ChakraLink>
    );
  };

  const controlledNavigate = (path: string) => {
    if (pathname.includes(path)) {
      navigate(`${path}`, { state: state });
    } else if (allowNavigate()) {
      navigate(`${path}`, { state: state });
    }
  };

  const handleFeedbackClick = () => {
    dispatch(
      openModal({
        modalType: ModalTypes.FEEDBACK,
        modalProps: {
          feedbackType: FEEDBACK_TYPE.SUBMIT_FEEDBACK,
        },
      })
    );
  };

  return (
    <NavigationPanel
      type="studio"
      aria-expanded="false"
      defaultIndex={isDataManger ? 0 : 1}
    >
      <NavigationPanelHeader
        className="cursor-pointer relative"
        // onClick={navigateToHome}
        logo={<LogoLink />}
      >
        <LogoTextLink />
        <NavigationPanelToggle
          hideOnCollapse
          className="absolute -right-2"
          style={{ transform: "rotate(180deg) translateY(4px)" }}
        />
      </NavigationPanelHeader>
      <NavigationPanelBody>
        <NavigationPanelItem
          icon={<Icon as={MdOutlineDns} />}
          label={"Data Manager"}
          // info={hasSheetSelection ? sheetSelectionInfo : undefined}
          selected={isActive("/data-manager")}
          onClick={controlledNavigate.bind(null, "data-manager")}
        />
        <NavigationPanelItem
          icon={<Icon as={MdOutlineAccountTree} />}
          label={"Workflow"}
          isDisabled={!workflowId}
          selected={isActive("/editor")}
          onClick={controlledNavigate.bind(null, `editor/${workflowId}`)}
        />
        {showGenie && (
          <NavigationPanelItem
            icon={<Icon as={AskClootrackIcon} />}
            label="Genie"
            selected={isActive("/genie")}
            onClick={navigateToAskclootrack}
          />
        )}
        <NavigationPanelItem
          icon={<Icon as={MdDashboard} />}
          label={"Dashboard"}
          selected={isActive("dashboard")}
          onClick={controlledNavigate.bind(null, "dashboard?tab=0")}
        />
      </NavigationPanelBody>
      <NavigationPanelFooter>
        <NavigationPanelToggle
          label="Collapse"
          stateChange={onToggleSidebar}
          hideOnExpand
        />
        <NavigationPanelItem
          label="Submit Feedback"
          icon={<Icon as={MdsRateReviewRound} className="stroke-[22]" />}
          onClick={handleFeedbackClick}
        />
        <NavigationPanelItem
          label="Contact Us"
          icon={<Icon as={MdsCalendarMonthRound} className="stroke-[22]" />}
          as="a"
          href="https://clootrack.com/request-demo"
          target="_blank"
          rel="noopener noreferrer"
        />
      </NavigationPanelFooter>
    </NavigationPanel>
  );
};
